import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { EditorCanvas } from "@/features/scene-editor/EditorCanvas";
import { useEditorStore } from "@/features/scene-editor/editor-store";
import { ObjectPropertyPanel } from "@/features/scene-editor/ObjectPropertyPanel";
import { PalettePanel } from "@/features/scene-editor/PalettePanel";

/**
 * シーンエディタ全体ページ。
 *
 * レイアウト:
 *   ┌──────────┬───────────────────────┬────────────────┐
 *   │ パレット   │ EditorCanvas (Pixi)   │ プロパティ      │
 *   │ + 操作    │                        │ + 検証エラー    │
 *   └──────────┴───────────────────────┴────────────────┘
 */
export function SceneEditorPage() {
  const issues = useEditorStore((s) => s.issues);
  const loadFromSim = useEditorStore((s) => s.loadFromSim);
  const applyToSim = useEditorStore((s) => s.applyToSim);
  const discard = useEditorStore((s) => s.discard);
  const resetDraft = useEditorStore((s) => s.resetDraft);
  const importJson = useEditorStore((s) => s.importJson);
  const exportJson = useEditorStore((s) => s.exportJson);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "info" | "error"; msg: string } | null>(null);

  // 初回 mount 時に sim の現在 scene を draft に取り込み
  useEffect(() => {
    loadFromSim();
  }, [loadFromSim]);

  const handleSave = () => {
    const errs = issues.filter((i) => i.level === "error");
    if (errs.length > 0) {
      setFeedback({ kind: "error", msg: `保存できません: ${errs.length} 件のエラーを解消してください` });
      return;
    }
    applyToSim();
    setFeedback({ kind: "info", msg: "シーンを反映しました (sim をリセット)" });
  };

  const handleExport = () => {
    const json = exportJson();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factory-scene-${new Date().toISOString().slice(0, 19).replace(/:/g, "")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setFeedback({ kind: "info", msg: "JSON をダウンロードしました" });
  };

  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      importJson(text);
      setFeedback({ kind: "info", msg: `${file.name} を読み込みました` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setFeedback({ kind: "error", msg: `Import 失敗: ${message}` });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* ヘッダ操作行 */}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: "#ffffff",
        }}
      >
        <Box sx={{ fontWeight: 700, fontSize: 14 }}>Scene Editor</Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<UndoRoundedIcon />} onClick={discard}>
            破棄
          </Button>
          <Button size="small" variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetDraft}>
            既定にリセット
          </Button>
          <Button size="small" variant="outlined" startIcon={<UploadRoundedIcon />} onClick={handleImportClick}>
            JSON Import
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={handleExport}>
            JSON Export
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            style={{ display: "none" }}
            onChange={handleImportChange}
          />
          <Button size="small" variant="contained" color="primary" startIcon={<SaveRoundedIcon />} onClick={handleSave}>
            保存して sim 反映
          </Button>
        </Stack>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.kind === "error" ? "error" : "success"}
          onClose={() => setFeedback(null)}
          sx={{ borderRadius: 0 }}
        >
          {feedback.msg}
        </Alert>
      )}

      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        <PalettePanel />
        <Box sx={{ flex: 1, position: "relative", minWidth: 0 }}>
          <EditorCanvas />
        </Box>
        <Box
          sx={{
            width: 320,
            borderLeft: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* 検証エラー */}
          {issues.length > 0 && (
            <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", maxHeight: 180, overflow: "auto" }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                検証 ({issues.length})
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {issues.map((i) => (
                  <Alert
                    key={`${i.level}|${i.objectId ?? "-"}|${i.message}`}
                    severity={i.level === "error" ? "error" : "warning"}
                    sx={{ py: 0.25 }}
                  >
                    {i.message}
                  </Alert>
                ))}
              </Stack>
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ObjectPropertyPanel />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
